import {useSendNotificationData} from '@/components/Drawer/hooks/useSendNotificationData'
import {AutoComplete} from '@/components/SendNotification/AutoComplete'
import {CustomImageSelector} from '@/components/SendNotification/CustomImageSelector'
import {InputWithCharLimit} from '@/components/SendNotification/InputWithCharLimit'
import {LandingPageSelector} from '@/components/SendNotification/LandingPageSelector'
import {AddLanguageButton, LanguageSelector} from '@/components/SendNotification/LanguageSelector'
import {RecipientDropDown} from '@/components/SendNotification/RecipientDropDown'
import {useJourneyMetaConfig} from '@/hooks/useJourneyMetaConfig'
import {NodeTypeEnum} from '@/types/NodeTypeEnum'
import {GetSendEmailStepConfigResponse} from '@/types/StepConfigParams'
import {StepSendEmailConfig} from '@/types/Steps'
import React from 'react'
import {useIntl} from 'react-intl'

interface SendEmailStepConfigParamsProps {
  getData: () => GetSendEmailStepConfigResponse
}

export default function SendEmailStepConfigParams({getData}: SendEmailStepConfigParamsProps): React.JSX.Element {
  const {formatMessage} = useIntl()
  const {isPublishedOrCompleted} = useJourneyMetaConfig()

  const {
    config: tempConfig,
    onChangeRecipient,
    onChangeSubject,
    onChangeMessage,
    onChangeCustomImage,
    onChangeLandingPage,
  } = useSendNotificationData(NodeTypeEnum.sendEmail)
  const config = tempConfig as StepSendEmailConfig

  const customImageOptions = getData()?.images ?? []
  const landingPageOptions = getData()?.landingPages ?? []
  return (
    <div className="flex w-full max-w-xs flex-col gap-4" data-testid="send-email-step-config-params">
      <RecipientDropDown
        value={config?.to}
        label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.RECIPIENT_LABEL'})}
        onChange={onChangeRecipient}
        required
      />
      <InputWithCharLimit
        id="send-notification-subject"
        inputValue={config?.subject}
        placeholder={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SUBJECT_PLACEHOLDER'})}
        onChangeInput={onChangeSubject}
        displayCharLimit={false}
        inputDisabled={isPublishedOrCompleted}
        label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SUBJECT_LABEL'})}
        required
      />

      <LanguageSelector label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.LANGUAGE_LABEL'})} />

      <AutoComplete
        inputId="send-notification-message"
        inputValue={config?.message}
        onChangeInput={onChangeMessage}
        label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.MESSAGE_LABEL'})}
        required
      />

      <CustomImageSelector
        label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_PUSH_IMAGE'})}
        options={customImageOptions}
        value={config?.imageId || customImageOptions[0]?.id}
        onChange={onChangeCustomImage}
        disabled={isPublishedOrCompleted}
      />

      <LandingPageSelector
        label={formatMessage({id: 'SEND_NOTIFICATION_CONTENT.SELECT_LANDING_PAGE'})}
        options={landingPageOptions}
        value={config?.landingPageId || landingPageOptions[0]?.id}
        onChange={onChangeLandingPage}
        disabled={isPublishedOrCompleted}
      />

      <AddLanguageButton data-testid="add-aditional-language-button" />
    </div>
  )
}
